import React, { useEffect, useRef, useState } from 'react';

import { ActionIcon, Badge, Box, Center, Flex, Radio, Skeleton, Table } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { CountryFlagImage } from 'components/CountryFlagImage';
import { NoDataFound } from 'components/NoDataFound';
import { PhoneNumberConflictErrorModal } from 'components/PhoneNumberConflictErrorModal';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { TelephonyService } from 'services/telephony-service';
import { PHONE_NUMBERS_QUERY_KEY } from 'shared/constants/query-keys';
import { DeleteElementConflictDto } from 'shared/interfaces/General';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { PhoneNumber } from 'shared/interfaces/Telephony';

interface PhoneNumberTableProps {
  phoneNumbers: PhoneNumber[];
  selectedNumber: string | undefined;
  isLoading: boolean;
  onRowClick: (phoneNumber: PhoneNumber) => void;
  disableSelect?: boolean;
  enableDelete?: boolean;
}

export function PhoneNumberTable(phoneNumberTableProps: PhoneNumberTableProps) {
  const { phoneNumbers, selectedNumber, onRowClick, isLoading, disableSelect, enableDelete } = phoneNumberTableProps;

  const { t } = useTranslation();
  const previouslySelectedNumbers = useRef<Set<string>>(new Set(selectedNumber));

  const [conflictedSmartAssistants, setConflictedSmartAssistants] = useState<SmartAssistant[]>([]);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [deletingPhoneNumberId, setDeletingPhoneNumberId] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  const deletePhoneNumberMutation = useMutation({
    mutationFn: TelephonyService.deleteNumber,
    onMutate: (phoneNumberId: string) => {
      setDeletingPhoneNumberId(phoneNumberId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PHONE_NUMBERS_QUERY_KEY] });
      setDeletingPhoneNumberId(undefined);
      NotificationService.showResult(true, 'FEED_BACK.TELEPHONY.SUCCESS.DELETE');
    },
    onError: (error: AxiosError) => {
      setDeletingPhoneNumberId(undefined);
      if (error.response?.status === HttpStatusCode.Conflict) {
        const conflictData = error.response?.data as DeleteElementConflictDto;
        if (!conflictData.smartAssistants) return;
        setConflictedSmartAssistants(conflictData.smartAssistants);
        setErrorModalOpen(true);
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.FAILURE.CONFLICT');
      } else {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.FAILURE.DELETE');
      }
    }
  });

  useEffect(() => {
    if (selectedNumber) previouslySelectedNumbers.current.add(selectedNumber);
  }, [selectedNumber]);

  const renderLoadingRow = () => (
    <tr>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Skeleton height={20} radius="sm" />
      </td>
      <td>
        <Center>
          <Skeleton height={20} radius="sm" circle />
        </Center>
      </td>
    </tr>
  );

  const renderEmptyState = () => {
    return (
      <tr>
        <td colSpan={4}>
          <NoDataFound
            pt="xl"
            size="xs"
            transparentIconBackground
            icon={<IconPlus size={24} color="gray" />}
            title={t('SMART_ASSISTANT.TELEPHONY_SECTION.EMPTY_STATE.TITLE')}
            subTitle={t('SMART_ASSISTANT.TELEPHONY_SECTION.EMPTY_STATE.DESCRIPTION')}
          />
        </td>
      </tr>
    );
  };

  const capitalize = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const renderPhoneNumberRow = (phoneNumber: PhoneNumber) => {
    // Phone is already in use if it is assigned to a smart assistant and it was not previously selected.
    // If the phone was previously selected, it is still valid to select it again.
    const isPhoneAlreadyInUse =
      !!phoneNumber.deploymentConfiguration?.smartAssistantId &&
      !previouslySelectedNumbers.current.has(phoneNumber._id);

    return (
      <tr
        key={phoneNumber._id}
        style={{
          opacity: isPhoneAlreadyInUse ? 0.5 : 1,
          cursor: isPhoneAlreadyInUse || disableSelect ? 'default' : 'pointer',
          lineHeight: '30px'
        }}
        onClick={() => {
          if (!isPhoneAlreadyInUse) {
            onRowClick(phoneNumber);
          }
        }}>
        <td>{phoneNumber.twilioPhoneNumberConfiguration.friendlyPhoneNumber}</td>
        <td>
          <Flex direction="row" align="center" gap="xs">
            <CountryFlagImage countryCode={phoneNumber.twilioPhoneNumberConfiguration.countryCode} />
            {phoneNumber.twilioPhoneNumberConfiguration.countryCode}
          </Flex>
        </td>
        <td>{capitalize(phoneNumber.provider)}</td>
        <td>
          <Badge color={isPhoneAlreadyInUse ? 'gray' : 'green'} tt="capitalize">
            {isPhoneAlreadyInUse
              ? t('SMART_ASSISTANT.TELEPHONY_SECTION.STATUS.IN_USE')
              : t('SMART_ASSISTANT.TELEPHONY_SECTION.STATUS.AVAILABLE')}
          </Badge>
        </td>
        {enableDelete && (
          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
            <Center>
              <ActionIcon
                loading={deletingPhoneNumberId === phoneNumber._id}
                onClick={() => deletePhoneNumberMutation.mutate(phoneNumber._id)}>
                <IconTrash color="red" />
              </ActionIcon>
            </Center>
          </td>
        )}
        {!disableSelect && (
          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
            {!isPhoneAlreadyInUse ? (
              <Center>
                <Radio
                  value={phoneNumber._id}
                  checked={selectedNumber === phoneNumber._id}
                  onChange={() => {
                    onRowClick(phoneNumber);
                  }}
                />
              </Center>
            ) : (
              <Box
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '1px solid #ced4da',
                  opacity: 0.5,
                  margin: '0 auto'
                }}
              />
            )}
          </td>
        )}
      </tr>
    );
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.NUMBER')}</th>
            <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.COUNTRY')}</th>
            <th>{t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.PROVIDER')}</th>
            <th style={{ paddingLeft: '23px' }}>{t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.STATUS')}</th>
            {enableDelete && (
              <th style={{ width: '100px', textAlign: 'center' }}>
                {t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.REMOVE')}
              </th>
            )}
            {!disableSelect && (
              <th style={{ width: '100px', textAlign: 'center' }}>
                {t('SMART_ASSISTANT.TELEPHONY_SECTION.TABLE.SELECT')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? renderLoadingRow()
            : phoneNumbers.length === 0
            ? renderEmptyState()
            : phoneNumbers.map((phoneNumber) => renderPhoneNumberRow(phoneNumber))}
        </tbody>
      </Table>
      {errorModalOpen && (
        <PhoneNumberConflictErrorModal
          errorModalOpened={errorModalOpen}
          conflictedSmartAssistants={conflictedSmartAssistants}
          onClose={() => {
            setErrorModalOpen(false);
            setConflictedSmartAssistants([]);
          }}
        />
      )}
    </>
  );
}
